// Production-safe logging helper. Every argument is walked recursively
// and any field whose key matches /passphrase|password|key|chunk_id|nonce|salt/i
// is replaced with the string "[redacted]". Uint8Arrays outside redacted
// fields pass through unchanged; inside them, they're replaced.
//
// In dev builds (import.meta.env.DEV) we skip redaction so the developer
// can see real values when debugging. Production Playwright runs assert
// that redaction is active — see web/e2e/m9-console-leaks.spec.ts.

const SENSITIVE_KEY = /passphrase|password|key|chunk_?id|nonce|salt/i;
const REDACTION_MAX_DEPTH = 6;

function redact(value: unknown, depth = 0): unknown {
  if (depth > REDACTION_MAX_DEPTH) return "[redacted:depth]";
  if (value === null || typeof value !== "object") return value;
  if (value instanceof Uint8Array) return value;
  if (Array.isArray(value)) {
    return value.map((v) => redact(v, depth + 1));
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    if (SENSITIVE_KEY.test(k)) {
      out[k] = "[redacted]";
    } else {
      out[k] = redact(v, depth + 1);
    }
  }
  return out;
}

/**
 * Dev/prod toggle kept in a mutable export so tests can flip it.
 * Production code never touches this — it reads once at module load.
 */
export const __logMode = { isProd: !import.meta.env.DEV };

const redactIfProd = (v: unknown) => (__logMode.isProd ? redact(v) : v);

export const log = {
  info: (...args: unknown[]) => console.info(...args.map(redactIfProd)),
  warn: (...args: unknown[]) => console.warn(...args.map(redactIfProd)),
  error: (...args: unknown[]) => console.error(...args.map(redactIfProd)),
  debug: (...args: unknown[]) => {
    if (!__logMode.isProd) console.debug(...args);
  },
};

/** Exported only for tests — the transformation itself. */
export const __redactForTest = (v: unknown): unknown => redact(v);
